import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Separator } from "../ui/separator";
import { ImageWithFallback } from '../figma/ImageWithFallback';
import { 
  Brain, 
  ShoppingCart, 
  FileText, 
  TrendingUp, 
  Target, 
  Zap, 
  Shield, 
  BarChart3, 
  RefreshCw, 
  Users, 
  CheckCircle, 
  ArrowRight,
  Clock,
  DollarSign,
  AlertTriangle,
  Package,
  Settings,
  Award,
  Coins,
  FileCheck,
  Search
} from "lucide-react";

export function ProcPay() {
  return (
    <div className="w-full">
      {/* What is ProcPay Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl mb-8" style={{ color: 'black' }}>What is ProcPay?</h2>
          </div>

          <div className="max-w-4xl mx-auto text-center">
            <p className="text-lg leading-relaxed mb-6">
              <strong>ProcPay</strong> is an integrated, AI-powered procure-to-pay (P2P) platform built for retail and distribution enterprises. It streamlines every step from contract creation to payment settlement — combining ToT lifecycle management, scheme execution, and procurement operations into a single, modular system.
            </p>
            <p className="text-lg leading-relaxed">
              Whether you're managing suppliers, launching promotions, or reconciling payments, ProcPay ensures automation, compliance, and visibility across the value chain.
            </p>
          </div>
        </div>
      </section>

      {/* Modular Architecture Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#E8DDDA' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl mb-4 font-bold" style={{ color: 'black' }}>Modular Architecture</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Each module is interoperable and can be deployed standalone or as a suite.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* ToT Management */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#FF814C' }}>
                  <FileText className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-xl" style={{ color: 'black' }}>ToT Management</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                    <span>Contract creation &amp; smart templates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                    <span>Auto-parsing of documents/emails</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                    <span>Version control &amp; approval workflows</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                    <span>Gap analysis with AI suggestions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                    <span>Performance tracking by ToT type, supplier, region</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Scheme Management */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#FF814C' }}>
                  <Coins className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-xl" style={{ color: 'black' }}>Scheme Management</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                    <span>Real-time promo creation and editing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                    <span>Approval workflows by region/brand</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                    <span>Auto-reconciliation of claims</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                    <span>ROI tracking by scheme type</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                    <span>Predictive analysis on offer effectiveness</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Procure Management */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#FF814C' }}>
                  <Package className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-xl" style={{ color: 'black' }}>Procure Management</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                    <span>PO-to-Payment automation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                    <span>Multi-vendor sourcing workflows</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                    <span>Inventory sync &amp; demand insights</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                    <span>Dynamic approval rules</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                    <span>Vendor performance analytics</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features & Benefits Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl mb-4" style={{ color: 'black' }}>Features &amp; Benefits</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ backgroundColor: '#F6FBEA' }}>
                  <th className="text-left p-4 border" style={{ color: '#819A40' }}>Feature</th>
                  <th className="text-left p-4 border" style={{ color: '#819A40' }}>Value</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b hover:bg-gray-50">
                  <td className="p-4 border">
                    <div className="flex items-center gap-2">
                      <Brain className="h-5 w-5" style={{ color: '#FF814C' }} />
                      <span>AI-Driven Forecasting</span>
                    </div>
                  </td>
                  <td className="p-4 border">Predict demand, pricing gaps, and stock needs</td>
                </tr>
                <tr className="border-b hover:bg-gray-50">
                  <td className="p-4 border">
                    <div className="flex items-center gap-2">
                      <RefreshCw className="h-5 w-5" style={{ color: '#FF814C' }} />
                      <span>End-to-End Automation</span>
                    </div>
                  </td>
                  <td className="p-4 border">Reduce manual touchpoints and errors</td>
                </tr>
                <tr className="border-b hover:bg-gray-50">
                  <td className="p-4 border">
                    <div className="flex items-center gap-2">
                      <Settings className="h-5 w-5" style={{ color: '#FF814C' }} />
                      <span>ERP Integration</span>
                    </div>
                  </td>
                  <td className="p-4 border">Seamless with SAP, Oracle, MS Dynamics</td>
                </tr>
                <tr className="border-b hover:bg-gray-50">
                  <td className="p-4 border">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" style={{ color: '#FF814C' }} />
                      <span>Smart Dashboards</span>
                    </div>
                  </td>
                  <td className="p-4 border">Real-time KPIs, drill-down analytics</td>
                </tr>
                <tr className="border-b hover:bg-gray-50">
                  <td className="p-4 border">
                    <div className="flex items-center gap-2">
                      <Shield className="h-5 w-5" style={{ color: '#FF814C' }} />
                      <span>Compliance &amp; Audit Trail</span>
                    </div>
                  </td>
                  <td className="p-4 border">Ensure regulatory and internal controls</td>
                </tr>
                <tr className="border-b hover:bg-gray-50">
                  <td className="p-4 border">
                    <div className="flex items-center gap-2">
                      <FileCheck className="h-5 w-5" style={{ color: '#FF814C' }} />
                      <span>Document Parsing</span>
                    </div>
                  </td>
                  <td className="p-4 border">Auto-ingest contracts &amp; invoices via NLP</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Business Outcomes Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F6FBEA' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl mb-4 font-bold" style={{ color: 'black' }}>Business Outcomes</h2>
            <p className="text-xl text-gray-600">Trusted by top retailers for:</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="text-center border-0 shadow-lg" style={{ backgroundColor: 'white' }}>
              <CardContent className="p-6">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#FF814C' }}>
                  <Clock className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl mb-2 font-bold" style={{ color: 'black' }}>30%</h3>
                <p className="text-gray-600">faster contract-to-payment cycles</p>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg" style={{ backgroundColor: 'white' }}>
              <CardContent className="p-6">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#FF814C' }}>
                  <Target className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl mb-2 font-bold" style={{ color: 'black' }}>98%</h3>
                <p className="text-gray-600">reconciliation accuracy</p>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg" style={{ backgroundColor: 'white' }}>
              <CardContent className="p-6">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#FF814C' }}>
                  <TrendingUp className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl mb-2 font-bold" style={{ color: 'black' }}>40%</h3>
                <p className="text-gray-600">reduction in scheme disputes</p>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg" style={{ backgroundColor: 'white' }}>
              <CardContent className="p-6">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#FF814C' }}>
                  <DollarSign className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl mb-2 font-bold" style={{ color: 'black' }}>3x</h3>
                <p className="text-gray-600">ROI improvement on promotional campaigns</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl mb-4 font-bold" style={{ color: 'black' }}>How It Works</h2>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-4 text-center">
            <Badge variant="outline" className="px-4 py-2 text-lg border-2" style={{ borderColor: '#819A40', color: '#819A40' }}>
              Create ToT
            </Badge>
            <ArrowRight className="h-6 w-6" style={{ color: '#FF814C' }} />
            <Badge variant="outline" className="px-4 py-2 text-lg border-2" style={{ borderColor: '#819A40', color: '#819A40' }}>
              Launch Scheme
            </Badge>
            <ArrowRight className="h-6 w-6" style={{ color: '#FF814C' }} />
            <Badge variant="outline" className="px-4 py-2 text-lg border-2" style={{ borderColor: '#819A40', color: '#819A40' }}>
              Procure &amp; Track
            </Badge>
            <ArrowRight className="h-6 w-6" style={{ color: '#FF814C' }} />
            <Badge variant="outline" className="px-4 py-2 text-lg border-2" style={{ borderColor: '#819A40', color: '#819A40' }}>
              AI Recommends &amp; Optimizes
            </Badge>
            <ArrowRight className="h-6 w-6" style={{ color: '#FF814C' }} />
            <Badge variant="outline" className="px-4 py-2 text-lg border-2" style={{ borderColor: '#819A40', color: '#819A40' }}>
              Auto-Reconcile &amp; Pay
            </Badge>
          </div>
        </div>
      </section>

      {/* User Challenges Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#E8DDDA' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl mb-4 font-bold" style={{ color: 'black' }}>User Challenges We Solve</h2>
          </div>

          <div className="space-y-8">
            {/* Challenge 1 */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-4">
                  <AlertTriangle className="h-8 w-8 flex-shrink-0 mt-1" style={{ color: '#FF814C' }} />
                  <div>
                    <h3 className="text-xl mb-2" style={{ color: '#819A40' }}>Fragmented Systems, Siloed Workflows</h3>
                    <p className="text-gray-600 mb-4">
                      Most large retail and distribution businesses operate procurement, ToT contracts, and promotional schemes in disconnected systems. This leads to data loss, duplication, manual reconciliation, and costly misalignment between teams.
                    </p>
                    <h4 className="font-medium mb-2" style={{ color: '#819A40' }}>How ProcPay Solves It:</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                        <span>Unified platform combining contract, promotion, and procurement workflows</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                        <span>Single source of truth across finance, procurement, and category teams</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                        <span>Real-time data sharing and updates, eliminating email and spreadsheet chaos</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                        <span>Native ERP and CRM integration to ensure ecosystem harmony</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Challenge 2 */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-4">
                  <DollarSign className="h-8 w-8 flex-shrink-0 mt-1" style={{ color: '#FF814C' }} />
                  <div>
                    <h3 className="text-xl mb-2" style={{ color: '#819A40' }}>Delayed Settlements and Promo Leakage</h3>
                    <p className="text-gray-600 mb-4">
                      Without real-time reconciliation and performance tracking, promotions often run without ROI analysis. Settlements are delayed, overpayments occur, and vendors dispute claims — increasing operating costs and reducing brand trust.
                    </p>
                    <h4 className="font-medium mb-2" style={{ color: '#819A40' }}>How ProcPay Solves It:</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                        <span>Automated scheme reconciliation at SKU, invoice, or serial number level</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                        <span>AI-based dispute detection and resolution paths</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                        <span>Real-time dashboards to track live promotion ROI and payout status</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                        <span>Custom business rules to prevent leakage or duplication in payouts</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Challenge 3 */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-4">
                  <FileText className="h-8 w-8 flex-shrink-0 mt-1" style={{ color: '#FF814C' }} />
                  <div>
                    <h3 className="text-xl mb-2" style={{ color: '#819A40' }}>Inflexible Contracts and Manual Admin Overload</h3>
                    <p className="text-gray-600 mb-4">
                      ToT agreements are often buried in email chains and PDFs. Creating, reviewing, and managing them manually slows down sourcing and increases legal and compliance risk.
                    </p>
                    <h4 className="font-medium mb-2" style={{ color: '#819A40' }}>How ProcPay Solves It:</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                        <span>Smart ToT templates and rule-driven contract builder</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                        <span>NLP-based document/email parsing to auto-create agreements</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                        <span>Version tracking and workflow-based approvals</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#819A40' }} />
                        <span>Performance analytics tied directly to contract terms</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Client Impact Story Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl mb-4 font-bold" style={{ color: 'black' }}>Client Impact Story</h2>
          </div>

          <Card className="border-0 shadow-xl max-w-4xl mx-auto">
            <CardContent className="p-8">
              <div className="mb-6">
                <h3 className="text-xl mb-3" style={{ color: '#819A40' }}>
                  Case Study Example: "How a Fortune 500 retailer reduced P2P cycle time by 42% using ProcPay"
                </h3>
                <blockquote className="text-lg italic text-gray-600 mb-4 pl-4 border-l-4" style={{ borderColor: '#819A40' }}>
                  "ProcPay helped us streamline our vendor contracts, launch promotions faster, and bring clarity to settlement operations — all with audit-grade transparency."
                </blockquote>
                <cite className="text-gray-700">– Head of Procurement, Leading Consumer Electronics Brand</cite>
              </div>
              <div className="text-center">
                <Button 
                  variant="outline" 
                  className="border-2"
                  style={{ borderColor: '#819A40', color: '#819A40' }}
                >
                  Read Full Case Study →
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Final CTA Banner */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#819A40' }}>
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl text-white mb-6">
            "Ready to Automate Your Procure-to-Pay Lifecycle?"
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg" 
              className="text-white hover:opacity-90"
              style={{ backgroundColor: '#FF814C' }}
            >
              Book a Live Demo
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-white hover:bg-white"
              style={{ color: 'black' }}
            >
              Talk to Our Experts
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}